import * as styles from "./ProjectTable.css";

import { Provider, createStore, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@frontend/components/atoms/AlertDialog";
import { Badge } from "@frontend/components/atoms/Badge";
import type { BadgeVariant } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Panel } from "@frontend/components/atoms/Panel";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import {
  DataTable,
  createDataTableColumnHelper,
  useLocalDataTable,
} from "@frontend/components/organisms/DataTable";
import type { LocalDataTableColumn } from "@frontend/components/organisms/DataTable";
import { formatMinutes } from "@frontend/utils/duration";
import { Trans, useLingui } from "@lingui/react/macro";

import { ProjectEditorDialog } from "../ProjectEditorDialog";
import {
  deleteInterventionAtom,
  loadInterventionsAtom,
  projectListsAtom,
  refreshInterventionsAtom,
} from "../stores/lists";
import type { InterventionRow } from "../types";

export type InterventionListProps = {
  projectId: number;
};

const helper = createDataTableColumnHelper<InterventionRow>();

const TONES: Record<string, BadgeVariant> = {
  success: "success",
  info: "info",
  warning: "warning",
  error: "error",
};

/**
 * The interventions the subscription carries: what each is worth in hours, and
 * how far it is from being ready to run.
 */
export function InterventionList({
  projectId,
}: InterventionListProps): JSX.Element {
  const { t } = useLingui();

  const { interventions, interventionsLoading, error } =
    useAtomValue(projectListsAtom);

  const load = useSetAtom(loadInterventionsAtom);
  const refresh = useSetAtom(refreshInterventionsAtom);

  // undefined while nothing is open, null on the row that creates one.
  const [openedId, setOpenedId] = useState<number | null | undefined>();

  // The row waiting for an answer, kept whole so the question can name it.
  const [removing, setRemoving] = useState<InterventionRow | null>(null);

  const remove = useSetAtom(deleteInterventionAtom);

  // The trash the question grows from.
  const originRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    void load(projectId);
  }, [load, projectId]);

  // The rows are all here, so the search, the filters and the sort read them
  // in place rather than asking the server.
  const readers = useMemo(
    (): Record<string, LocalDataTableColumn<InterventionRow>> => ({
      label: { value: (intervention) => intervention.label },
      statusLabel: {
        value: (intervention) => intervention.statusLabel,
        searchable: false,
      },
      level: {
        value: (intervention) => intervention.level.label,
        searchable: false,
      },
      dateBegin: {
        value: (intervention) => intervention.dateBegin,
        searchable: false,
      },
      dateEnd: {
        value: (intervention) => intervention.dateEnd,
        searchable: false,
      },
      interventionDays: {
        value: (intervention) => intervention.interventionDays,
        searchable: false,
      },
      attributedMinutes: {
        value: (intervention) => intervention.attributedMinutes,
        searchable: false,
      },
    }),
    [],
  );

  const local = useLocalDataTable({
    rows: interventions,
    columns: readers,
    initialSorting: [{ id: "dateBegin", desc: true }],
  });

  // What the rows say, offered as the choices of the two filters.
  const choices = (read: (intervention: InterventionRow) => string | null) =>
    [...new Set(interventions.map(read).filter(Boolean))]
      .sort()
      .map((value) => ({ value: value as string, label: value as string }));

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("label", {
          header: t`Intervention`,
          meta: { minWidth: "220px", filter: "text", truncate: true },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("statusLabel", {
          header: t`Status`,
          meta: {
            minWidth: "100px",
            maxWidth: "180px",
            truncate: true,
            filter: {
              type: "select",
              options: choices((intervention) => intervention.statusLabel),
            },
          },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.display({
          id: "level",
          header: t`Readiness`,
          meta: {
            width: "140px",
            filter: {
              type: "select",
              options: choices((intervention) => intervention.level.label),
            },
          },
          cell: ({ row }) => (
            <Badge
              variant={TONES[row.original.level.tone] ?? "neutral"}
              size="sm"
            >
              {row.original.level.label}
            </Badge>
          ),
        }),
        helper.accessor("dateBegin", {
          header: t`From`,
          meta: { minWidth: "110px", maxWidth: "130px" },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("dateEnd", {
          header: t`To`,
          meta: { minWidth: "110px", maxWidth: "130px" },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("interventionDays", {
          header: t`Days`,
          meta: { width: "80px" },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("attributedMinutes", {
          header: t`Hours`,
          meta: { width: "90px" },
          cell: ({ getValue }) => formatMinutes(getValue()),
        }),
        helper.display({
          id: "actions",
          header: t`Action`,
          enableSorting: false,
          meta: { width: "80px" },
          cell: ({ row }) => (
            <span
              className={styles.rowActions}
              // The row itself opens the intervention, and this does not.
              onClick={(event) => event.stopPropagation()}
            >
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Delete the intervention`}
                      onClick={(event) => {
                        originRef.current =
                          event.currentTarget.getBoundingClientRect();
                        setRemoving(row.original);
                      }}
                    >
                      <Icon name="Trash2" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="left">
                  {t`Delete the intervention`}
                </TooltipContent>
              </Tooltip>
            </span>
          ),
        }),
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, interventions],
  );

  // Named rather than read inline: a lingui placeholder wants an identifier.
  const removed = removing?.label ?? "";

  return (
    <div className={styles.table}>
      {error ? (
        <p className={styles.alert} role="alert">
          {error}
        </p>
      ) : null}

      <Panel overflow="visible">
        <Toolbar wrap={false}>
          <ToolbarSection>
            <Button
              variant="link"
              size="sm"
              leftSection={<Icon name="Plus" size="sm" />}
              onClick={() => setOpenedId(null)}
            >
              {t`Add an intervention`}
            </Button>
          </ToolbarSection>

          <ToolbarSection className={styles.search}>
            <Input
              value={local.search}
              placeholder={t`Search an intervention`}
              leftSection={<Icon name="Search" size="sm" />}
              onValueChange={local.setSearch}
            />
          </ToolbarSection>
        </Toolbar>

        <DataTable
          columns={columns}
          data={local.rows}
          getRowId={(intervention) => String(intervention.id)}
          loading={interventionsLoading}
          loadingRowCount={4}
          emptyMessage={
            "" === local.search && 0 === local.columnFilters.length ? (
              <Trans>This subscription carries no intervention yet.</Trans>
            ) : (
              <Trans>No intervention matches this search.</Trans>
            )
          }
          size="sm"
          variant="ghost"
          horizontalSpacing="sm"
          verticalSpacing="sm"
          bordered={false}
          sorting={local.sorting}
          onSortingChange={local.onSortingChange}
          columnFilters={local.columnFilters}
          onColumnFiltersChange={local.onColumnFiltersChange}
          onRowClick={(intervention) => setOpenedId(intervention.id)}
        />
      </Panel>

      <AlertDialog
        open={null !== removing}
        originRef={originRef}
        onOpenChange={(next) => {
          if (!next) {
            setRemoving(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this intervention?</Trans>
            </AlertDialogTitle>

            <AlertDialogDescription>
              <Trans>
                {removed} goes with its work posts and their tasks. One whose
                tasks carry done or validated hours is refused, and the table
                says why.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>

            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (null !== removing) {
                  void remove(removing.id);
                }

                setRemoving(null);
              }}
            >
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Keyed on the record: the editor loads on mount, and two rows are two
          different interventions. */}
      {undefined === openedId ? null : (
        <NestedInterventionEditor
          key={openedId ?? "new"}
          projectId={openedId}
          subscriptionId={projectId}
          onClose={() => setOpenedId(undefined)}
          onSaved={() => {
            setOpenedId(undefined);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

/**
 * An intervention is a project too, and this editor keeps its form in
 * module-level atoms. The nested one is given a jotai store of its own, so the
 * subscription being edited behind it is left alone.
 */
function NestedInterventionEditor({
  projectId,
  subscriptionId,
  onClose,
  onSaved,
}: {
  projectId: number | null;
  subscriptionId: number;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [store] = useState(() => createStore());

  return (
    <Provider store={store}>
      <ProjectEditorDialog
        open
        onOpenChange={(next) => {
          if (!next) {
            onClose();
          }
        }}
        projectId={projectId}
        type="intervention"
        subscriptionId={subscriptionId}
        onSaved={onSaved}
        onCancel={onClose}
      />
    </Provider>
  );
}
